import React, { useState, useEffect } from 'react';
import { apiFetch } from './api';
import TaskDropdown from './taskDropdown';

const Zeitauswertung = ({ user }) => {
  const [eintraege, setEintraege] = useState([]);
  const [alleUsers, setAlleUsers] = useState([]);
  const [alleProduktionen, setAlleProduktionen] = useState([]);
  const [alleFormate, setAlleFormate] = useState([]);
  const [alleBackoffice, setAlleBackoffice] = useState([]);
  const [alleProjekte, setAlleProjekte] = useState([]);
  const [filterUser, setFilterUser] = useState('alle');
  const [filterMutter, setFilterMutter] = useState('alle');
  const [filterProjekt, setFilterProjekt] = useState('alle');
  const [loading, setLoading] = useState(true);

  const [zeitfenster, setZeitfenster] = useState('woche');
  const [beliebigVon, setBeliebigVon] = useState('');
  const [beliebigBis, setBeliebigBis] = useState('');
  const [beliebigOffen, setBeliebigOffen] = useState(false);

  const [editPopup, setEditPopup] = useState(null);
  const [editName, setEditName] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');

  const alleMutter = [
    ...alleProduktionen.map(p => ({ ...p, typ: 'produktion' })),
    ...alleFormate.map(f => ({ ...f, typ: 'format' })),
    ...alleBackoffice.map(b => ({ ...b, typ: 'backoffice' })),
  ];

  const getZeitraum = () => {
    const heute = new Date();
    if (zeitfenster === 'woche') {
      const tag = heute.getDay();
      const diffMo = tag === 0 ? -6 : 1 - tag;
      const montag = new Date(heute);
      montag.setDate(heute.getDate() + diffMo);
      montag.setHours(0, 0, 0, 0);
      const sonntag = new Date(montag);
      sonntag.setDate(montag.getDate() + 6);
      sonntag.setHours(23, 59, 59, 999);
      return { von: montag.toISOString(), bis: sonntag.toISOString() };
    }
    if (zeitfenster === 'monat') {
      const erster = new Date(heute.getFullYear(), heute.getMonth(), 1, 0, 0, 0, 0);
      const letzter = new Date(heute.getFullYear(), heute.getMonth() + 1, 0, 23, 59, 59, 999);
      return { von: erster.toISOString(), bis: letzter.toISOString() };
    }
    if (zeitfenster === 'beliebig' && beliebigVon && beliebigBis) {
      const von = new Date(beliebigVon); von.setHours(0, 0, 0, 0);
      const bis = new Date(beliebigBis); bis.setHours(23, 59, 59, 999);
      return { von: von.toISOString(), bis: bis.toISOString() };
    }
    return null;
  };

  useEffect(() => {
    const ladeStammdaten = async () => {
      const [usersRes, prodRes, formatRes, backRes, projekteRes] = await Promise.all([
        apiFetch('/users'), apiFetch('/produktionen'), apiFetch('/formate'),
        apiFetch('/backoffice'), apiFetch('/projekte'),
      ]);
      if (!usersRes || !prodRes || !formatRes || !backRes || !projekteRes) return;
      setAlleUsers(await usersRes.json());
      setAlleProduktionen(await prodRes.json());
      setAlleFormate(await formatRes.json());
      setAlleBackoffice(await backRes.json());
      setAlleProjekte(await projekteRes.json());
    };
    ladeStammdaten();
  }, []);

  const ladeEintraege = async () => {
    const zeitraum = getZeitraum();
    if (zeitfenster === 'beliebig' && (!beliebigVon || !beliebigBis)) return;
    if (!zeitraum) return;
    setLoading(true);
    let url = '/auswertung/zeiteintraege?';
    url += `&von=${encodeURIComponent(zeitraum.von)}&bis=${encodeURIComponent(zeitraum.bis)}`;
    if (filterUser !== 'alle') url += `&user_id=${filterUser}`;
    if (filterMutter !== 'alle') {
      const m = alleMutter.find(m => `${m.typ}-${m.id}` === filterMutter);
      if (m) url += `&mutter_typ=${m.typ}&mutter_id=${m.id}`;
    }
    if (filterProjekt !== 'alle') url += `&projekt_id=${filterProjekt}`;
    const res = await apiFetch(url);
    if (!res) return;
    const data = await res.json();
    setEintraege(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => {
    if (alleMutter.length >= 0) ladeEintraege();
  }, [filterUser, filterMutter, filterProjekt, alleMutter.length, zeitfenster, beliebigVon, beliebigBis]);

  const formatMin = (min) => {
    if (!min || min === 0) return '0min';
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const formatDatum = (iso) => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  const formatUhrzeit = (iso) => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const toLocalInput = (iso) => {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const kannBearbeiten = (e) => user.role === 'admin' || e.user_id === user.id;

  const editOeffnen = (e) => {
    setEditPopup(e);
    setEditName(e.name || '');
    setEditStart(toLocalInput(e.start_zeit));
    setEditEnd(toLocalInput(e.end_zeit));
    setEditTaskId(e.task_id || null);
    setEditTaskName(e.task_name || '');
  };

  const editSpeichern = async () => {
    await apiFetch(`/zeit/eintraege/${editPopup.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: editName,
        task_id: editTaskId || null,
        start_zeit: new Date(editStart).toISOString(),
        end_zeit: new Date(editEnd).toISOString()
      })
    });
    setEditPopup(null);
    ladeEintraege();
  };

  const eintragLoeschen = async (id) => {
    await apiFetch(`/zeit/eintraege/${id}`, { method: 'DELETE' });
    setEditPopup(null);
    ladeEintraege();
  };

  // ─── Auswertungen ─────────────────────────────────

  const gesamtMinuten = eintraege.reduce((sum, e) => sum + (e.minuten || 0), 0);

  const perTask = {};
  eintraege.forEach(e => {
    const key = e.task_id || `ohne-${e.id}`;
    if (!perTask[key]) {
      perTask[key] = { name: e.task_name || e.name || '–', projekt: e.projekt_name, mutter: e.mutter_name, farbe: e.mutter_farbe, minuten: 0 };
    }
    perTask[key].minuten += e.minuten || 0;
  });
  const taskListe = Object.values(perTask).sort((a, b) => b.minuten - a.minuten);

  const perPerson = {};
  eintraege.forEach(e => {
    if (!perPerson[e.user_id]) {
      perPerson[e.user_id] = { name: e.user_name, minuten: 0 };
    }
    perPerson[e.user_id].minuten += e.minuten || 0;
  });
  const personListe = Object.values(perPerson).sort((a, b) => b.minuten - a.minuten);

  const gefilterteProjekte = filterMutter === 'alle'
    ? alleProjekte
    : alleProjekte.filter(p => {
        const m = alleMutter.find(m => `${m.typ}-${m.id}` === filterMutter);
        return m ? p.parent_typ === m.typ && p.parent_id === m.id : true;
      });

  const bereichGruppen = [
    { label: 'Produktionen', items: alleProduktionen.map(p => ({ ...p, typ: 'produktion' })) },
    { label: 'Formatentwicklung', items: alleFormate.map(f => ({ ...f, typ: 'format' })) },
    { label: 'Backoffice', items: alleBackoffice.map(b => ({ ...b, typ: 'backoffice' })) },
  ];

  return (
    <div style={{ padding: '40px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
            Zeitauswertung
          </h2>
          <p style={{ fontSize: '14px', color: '#9b9b9b', margin: 0 }}>
            Alle erfassten Zeiten – gefiltert und summiert
          </p>
        </div>

        {/* Zeitfenster-Filter */}
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
            {[
              { key: 'woche', label: 'Diese Woche' },
              { key: 'monat', label: 'Dieser Monat' },
              { key: 'beliebig', label: 'Beliebig' },
            ].map(z => (
              <button key={z.key} onClick={() => { setZeitfenster(z.key); if (z.key === 'beliebig') setBeliebigOffen(true); else setBeliebigOffen(false); }} style={{
                padding: '7px 14px', border: 'none', fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                backgroundColor: zeitfenster === z.key ? '#1a1a1a' : '#ffffff',
                color: zeitfenster === z.key ? '#ffffff' : '#6b6b6b',
              }}>
                {z.label}
              </button>
            ))}
          </div>

          {zeitfenster === 'beliebig' && beliebigOffen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200,
              backgroundColor: '#ffffff', border: '1px solid #e8e8e8', borderRadius: '10px',
              padding: '16px', boxShadow: '0 8px 28px rgba(0,0,0,0.12)', minWidth: '280px'
            }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={inputLabel}>Von</label>
                  <input type="date" value={beliebigVon} onChange={(e) => setBeliebigVon(e.target.value)} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={inputLabel}>Bis</label>
                  <input type="date" value={beliebigBis} onChange={(e) => setBeliebigBis(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <button onClick={() => setBeliebigOffen(false)} style={{
                width: '100%', padding: '8px', backgroundColor: '#1a1a1a', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              }}>Anwenden</button>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

        {/* Filter Sidebar */}
        <div style={{
          width: '200px', flexShrink: 0, backgroundColor: '#ffffff',
          border: '1px solid #efefef', borderRadius: '10px', padding: '20px'
        }}>

          {/* Person */}
          <div style={{ marginBottom: '20px' }}>
            <p style={filterLabel}>Person</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div onClick={() => setFilterUser('alle')} style={filterItem(filterUser === 'alle')}>Alle</div>
              {alleUsers.map(u => (
                <div key={u.id} onClick={() => setFilterUser(String(u.id))} style={filterItem(filterUser === String(u.id))}>
                  {u.name}
                </div>
              ))}
            </div>
          </div>

          {/* Bereich gruppiert */}
          <div style={{ marginBottom: '20px' }}>
            <p style={filterLabel}>Bereich</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div onClick={() => { setFilterMutter('alle'); setFilterProjekt('alle'); }} style={filterItem(filterMutter === 'alle')}>Alle</div>
            </div>
            {bereichGruppen.map(gruppe => gruppe.items.length > 0 && (
              <div key={gruppe.label} style={{ marginTop: '12px' }}>
                <p style={{
                  fontSize: '9px', fontWeight: '600', color: '#c4c4c4',
                  textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px 0'
                }}>{gruppe.label}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {gruppe.items.map(m => (
                    <div key={`${m.typ}-${m.id}`}
                      onClick={() => { setFilterMutter(`${m.typ}-${m.id}`); setFilterProjekt('alle'); }}
                      style={{
                        ...filterItem(filterMutter === `${m.typ}-${m.id}`),
                        borderLeft: m.farbe ? `3px solid ${m.farbe}` : '3px solid transparent',
                        paddingLeft: '8px'
                      }}>
                      {m.name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Projekt */}
          <div>
            <p style={filterLabel}>Projekt</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div onClick={() => setFilterProjekt('alle')} style={filterItem(filterProjekt === 'alle')}>Alle</div>
              {gefilterteProjekte.map(p => (
                <div key={p.id} onClick={() => setFilterProjekt(String(p.id))} style={filterItem(filterProjekt === String(p.id))}>
                  {p.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hauptbereich */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Zusammenfassung */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', padding: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>Gesamt</p>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0, letterSpacing: '-1px' }}>{formatMin(gesamtMinuten)}</p>
            </div>
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', padding: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>Einträge</p>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0, letterSpacing: '-1px' }}>{eintraege.length}</p>
            </div>
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', padding: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>Tasks</p>
              <p style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0, letterSpacing: '-1px' }}>{taskListe.length}</p>
            </div>
          </div>

          {/* Summe pro Person */}
          {personListe.length > 0 && filterUser === 'alle' && (
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', padding: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 12px 0' }}>Pro Person</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {personListe.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#f0f0ee',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '600', color: '#6b6b6b', flexShrink: 0
                    }}>{p.name.charAt(0).toUpperCase()}</div>
                    <span style={{ fontSize: '13px', color: '#1a1a1a', flex: 1 }}>{p.name}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>{formatMin(p.minuten)}</span>
                    <div style={{ width: '80px', height: '4px', backgroundColor: '#f0f0ee', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.round((p.minuten / gesamtMinuten) * 100)}%`, height: '100%', backgroundColor: '#1a1a1a', borderRadius: '2px' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summe pro Task */}
          {taskListe.length > 0 && (
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', padding: '20px' }}>
              <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 12px 0' }}>Pro Task</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {taskListe.map((t, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px', backgroundColor: '#fafafa', borderRadius: '6px',
                    borderLeft: t.farbe ? `3px solid ${t.farbe}` : '3px solid #e8e8e8'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: t.name === '–' ? '#9b9b9b' : '#1a1a1a', margin: '0 0 2px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                      </p>
                      <p style={{ fontSize: '11px', color: '#9b9b9b', margin: 0 }}>
                        {t.mutter ? `${t.mutter} · ` : ''}{t.projekt || ''}
                      </p>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', flexShrink: 0 }}>{formatMin(t.minuten)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alle Einträge */}
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', padding: '20px' }}>
            <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 12px 0' }}>
              Alle Einträge ({eintraege.length})
            </p>
            {loading ? (
              <p style={{ fontSize: '14px', color: '#c4c4c4' }}>Lädt...</p>
            ) : zeitfenster === 'beliebig' && (!beliebigVon || !beliebigBis) ? (
              <p style={{ fontSize: '14px', color: '#c4c4c4' }}>Bitte Von und Bis Datum wählen.</p>
            ) : eintraege.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#c4c4c4' }}>Keine Einträge in diesem Zeitraum.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {eintraege.map(e => {
                  const ohneTask = !e.task_id;
                  return (
                    <div key={e.id} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 14px', borderRadius: '8px', backgroundColor: '#fafafa',
                      borderLeft: ohneTask ? '3px dashed #d4d4d4' : e.mutter_farbe ? `3px solid ${e.mutter_farbe}` : '3px solid transparent'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '13px', fontWeight: '500', color: ohneTask ? '#9b9b9b' : '#1a1a1a', margin: '0 0 2px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.name || e.task_name || '–'}
                          {ohneTask && <span style={{ fontSize: '10px', color: '#c4c4c4', marginLeft: '6px', fontWeight: '400' }}>Keine Task</span>}
                        </p>
                        <p style={{ fontSize: '11px', color: '#9b9b9b', margin: 0 }}>
                          {e.mutter_name ? `${e.mutter_name} · ` : ''}{e.projekt_name ? `${e.projekt_name} · ` : ''}{e.task_name ? `${e.task_name} · ` : ''}{e.user_name}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 2px 0' }}>{formatMin(e.minuten)}</p>
                        <p style={{ fontSize: '11px', color: '#9b9b9b', margin: 0 }}>
                          {formatDatum(e.start_zeit)} · {formatUhrzeit(e.start_zeit)}–{formatUhrzeit(e.end_zeit)}
                        </p>
                      </div>
                      {kannBearbeiten(e) && (
                        <button onClick={() => editOeffnen(e)} style={{
                          padding: '4px 10px', backgroundColor: 'transparent', border: '1px solid #e8e8e8',
                          borderRadius: '6px', fontSize: '11px', color: '#6b6b6b', cursor: 'pointer', flexShrink: 0
                        }}>Bearbeiten</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Edit-Popup */}
      {editPopup && (
        <div onClick={() => setEditPopup(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: '#ffffff', borderRadius: '14px', width: '100%', maxWidth: '420px',
            padding: '32px', boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
            borderTop: editPopup.mutter_farbe ? `4px solid ${editPopup.mutter_farbe}` : '4px solid #e8e8e8'
          }}>
            <p style={{ fontSize: '11px', fontWeight: '500', color: editPopup.mutter_farbe || '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px 0' }}>
              {editPopup.mutter_name || editPopup.projekt_name || 'Kein Bereich'}
            </p>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 6px 0', letterSpacing: '-0.3px' }}>
              {editPopup.task_name || editPopup.name || '– kein Name –'}
            </h3>
            <p style={{ fontSize: '12px', color: '#9b9b9b', margin: '0 0 20px 0' }}>
              {editPopup.user_name}
            </p>

            <div style={{ marginBottom: '14px' }}>
              <label style={inputLabel}>Beschreibung</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={inputLabel}>Task (optional)</label>
              <TaskDropdown
                selectedTaskId={editTaskId}
                selectedTaskName={editTaskName}
                onSelect={(id, tname) => { setEditTaskId(id); setEditTaskName(tname); }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
              <div style={{ flex: 1 }}>
                <label style={inputLabel}>Von</label>
                <input type="datetime-local" value={editStart} onChange={(e) => setEditStart(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={inputLabel}>Bis</label>
                <input type="datetime-local" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={editSpeichern} style={{
                padding: '10px 20px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
              }}>Speichern</button>
              <button onClick={() => eintragLoeschen(editPopup.id)} style={{
                padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ffdddd',
                borderRadius: '8px', fontSize: '13px', color: '#cc0000', cursor: 'pointer'
              }}>Löschen</button>
              <button onClick={() => setEditPopup(null)} style={{
                padding: '10px 20px', backgroundColor: 'transparent', color: '#6b6b6b',
                border: '1px solid #e8e8e8', borderRadius: '8px', fontSize: '13px', cursor: 'pointer'
              }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const filterLabel = {
  fontSize: '10px', fontWeight: '600', color: '#9b9b9b',
  textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0'
};
const filterItem = (aktiv) => ({
  fontSize: '12px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer',
  backgroundColor: aktiv ? '#f0f0ee' : 'transparent',
  color: aktiv ? '#1a1a1a' : '#6b6b6b',
  fontWeight: aktiv ? '500' : '400',
});
const inputLabel = {
  display: 'block', fontSize: '11px', fontWeight: '500', color: '#6b6b6b',
  marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
};
const inputStyle = {
  width: '100%', padding: '9px 12px', fontSize: '13px', border: '1px solid #e8e8e8',
  borderRadius: '8px', outline: 'none', boxSizing: 'border-box'
};

export default Zeitauswertung;